/**
 * API Endpoints
 *
 * Centralized API endpoint constants.
 * Provides type-safe endpoint generation functions.
 */

/**
 * API endpoint constants organized by domain
 */
export const endpoints = {
  /**
   * Project endpoints
   */
  projects: {
    list: '/api/projects',
    create: '/api/projects',
    get: (id: string) => `/api/projects/${id}`,
    update: (id: string) => `/api/projects/${id}`,
    delete: (id: string) => `/api/projects/${id}`,
    clearData: (id: string) => `/api/projects/${id}/clear-data`,
    upload: (id: string) => `/api/projects/${id}/upload`,
    checkConflicts: (id: string) => `/api/projects/${id}/check-conflicts`,
    deleteReport: (projectId: string, reportId: string) =>
      `/api/projects/${projectId}/reports/${reportId}`,
    analyze: (id: string) => `/api/projects/${id}/analyze`,
    runPipeline: (id: string) => `/api/projects/${id}/pipeline`,
    analyses: (id: string) => `/api/projects/${id}/analyses`,
    trends: (id: string) => `/api/projects/${id}/analyses/trends`,
    stats: (id: string) => `/api/projects/${id}/stats`,
  },

  /**
   * Report Config endpoints
   */
  reportConfigs: {
    list: (projectId: string) => `/api/projects/${projectId}/report-configs`,
    create: (projectId: string) => `/api/projects/${projectId}/report-configs`,
    update: (projectId: string, configId: string) =>
      `/api/projects/${projectId}/report-configs/${configId}`,
    delete: (projectId: string, configId: string) =>
      `/api/projects/${projectId}/report-configs/${configId}`,
  },

  /**
   * Report endpoints (multi-report type system)
   */
  reports: {
    list: (projectId: string) => `/api/projects/${projectId}/reports`,
    get: (projectId: string, reportId: string) =>
      `/api/projects/${projectId}/reports/${reportId}`,
    delete: (projectId: string, reportId: string) =>
      `/api/projects/${projectId}/reports/${reportId}`,
  },

  /**
   * Compliance endpoints
   */
  compliance: {
    getMetrics: (projectId: string, reportConfigId?: string) =>
      reportConfigId
        ? `/api/projects/${projectId}/compliance/${reportConfigId}`
        : `/api/projects/${projectId}/compliance`,
    getHistory: (projectId: string, reportConfigId: string) =>
      `/api/projects/${projectId}/compliance/${reportConfigId}/history`,
    getCalendar: (projectId: string, reportConfigId: string) =>
      `/api/projects/${projectId}/compliance/${reportConfigId}/calendar`,
    getTrend: (projectId: string, reportConfigId: string) =>
      `/api/projects/${projectId}/compliance/${reportConfigId}/trend`,
    createSnapshot: (projectId: string, reportConfigId: string) =>
      `/api/projects/${projectId}/compliance/${reportConfigId}/snapshot`,
  },

  /**
   * SharePoint integration endpoints
   */
  sharepoint: {
    configure: (projectId: string) => `/api/projects/${projectId}/sharepoint/configure`,
    getStatus: (projectId: string) => `/api/projects/${projectId}/sharepoint/status`,
    testConnection: (projectId: string) =>
      `/api/projects/${projectId}/sharepoint/test-connection`,
    sync: (projectId: string) => `/api/projects/${projectId}/sharepoint/sync`,
    getHistory: (projectId: string) => `/api/projects/${projectId}/sharepoint/history`,
    disconnect: (projectId: string) => `/api/projects/${projectId}/sharepoint/disconnect`,
    eligibleProjects: '/api/projects/sharepoint/eligible',
    syncAll: '/api/projects/sharepoint/sync-all',
  },

  /**
   * Global Report Config endpoints (organization-level defaults)
   */
  globalReportConfigs: {
    list: '/api/global-report-configs',
    create: '/api/global-report-configs',
    get: (id: string) => `/api/global-report-configs/${id}`,
    getByType: (reportType: string) =>
      `/api/global-report-configs/by-type/${reportType}`,
    update: (id: string) => `/api/global-report-configs/${id}`,
    delete: (id: string) => `/api/global-report-configs/${id}`,
    applyToAll: (id: string) => `/api/global-report-configs/${id}/apply-to-all`,
  },

  /**
   * Analysis endpoints
   */
  analyses: {
    get: (id: string) => `/api/analyses/${id}`,
    delete: (id: string) => `/api/analyses/${id}`,
    compare: '/api/analyses/compare',
    getByHealthScore: '/api/analyses/by-health-score',
  },

  /**
   * Delivery Unit endpoints
   */
  deliveryUnits: {
    list: '/api/delivery-units',
  },

  /**
   * Auth endpoints
   */
  auth: {
    me: '/api/auth/me',
    mockUsers: '/api/auth/mock-users',
  },

  /**
   * Admin endpoints (SUPER_USER only)
   */
  admin: {
    users: '/api/admin/users',
    user: (id: string) => `/api/admin/users/${id}`,
    impersonate: (id: string) => `/api/admin/impersonate/${id}`,
    pipelineLogs: '/api/admin/pipeline-logs',
  },

  /**
   * Pipeline log endpoints
   */
  pipelineLogs: {
    forProject: (projectId: string) => `/api/projects/${projectId}/pipeline-logs`,
    admin: '/api/admin/pipeline-logs',
  },

  /**
   * Project membership endpoints
   */
  members: {
    list: (projectId: string) => `/api/projects/${projectId}/members`,
    add: (projectId: string) => `/api/projects/${projectId}/members`,
    update: (projectId: string, userId: string) => `/api/projects/${projectId}/members/${userId}`,
    remove: (projectId: string, userId: string) => `/api/projects/${projectId}/members/${userId}`,
  },

  /**
   * Health check endpoint
   */
  health: '/api/health',

  /**
   * Cross-project report endpoints
   */
  crossProjectReports: {
    compliance: '/api/reports/compliance',
  },

  /**
   * ADO (Azure DevOps) integration endpoints
   */
  ado: {
    getConfig:     (projectId: string) => `/api/projects/${projectId}/ado/config`,
    upsertConfig:  (projectId: string) => `/api/projects/${projectId}/ado/config`,
    deleteConfig:  (projectId: string) => `/api/projects/${projectId}/ado/config`,
    triggerSync:   (projectId: string) => `/api/projects/${projectId}/ado/sync`,
    getSyncStatus: (projectId: string) => `/api/projects/${projectId}/ado/sync/status`,
    listWorkItems: (projectId: string) => `/api/projects/${projectId}/ado/workitems`,
    getWorkItem:   (projectId: string, adoId: number) => `/api/projects/${projectId}/ado/workitems/${adoId}`,
    getComments:   (projectId: string, adoId: number) => `/api/projects/${projectId}/ado/workitems/${adoId}/comments`,
    getSummary:      (projectId: string) => `/api/projects/${projectId}/ado/summary`,
    runPipeline:     (projectId: string) => `/api/projects/${projectId}/ado/pipeline`,
    adoTrend:        (projectId: string) => `/api/projects/${projectId}/ado/trend`,
    adoTrendHistory: (projectId: string) => `/api/projects/${projectId}/ado/trend/history`,
  },

  /**
   * PMI (Project Management Index) endpoints
   */
  pmi: {
    get:       (projectId: string) => `/api/projects/${projectId}/pmi`,
    history:   (projectId: string) => `/api/projects/${projectId}/pmi/history`,
    compute:   (projectId: string) => `/api/projects/${projectId}/pmi/compute`,
    breakdown: (projectId: string) => `/api/projects/${projectId}/pmi/breakdown`,
  },

  /**
   * Per-project Dashboard endpoints
   */
  dashboard: {
    overview:             (projectId: string) => `/api/projects/${projectId}/dashboard/overview`,
    meetings:             (projectId: string) => `/api/projects/${projectId}/dashboard/meetings`,
    financials:           (projectId: string) => `/api/projects/${projectId}/dashboard/financials`,
    actionItems:          (projectId: string) => `/api/projects/${projectId}/dashboard/action-items`,
    consistency:          (projectId: string) => `/api/projects/${projectId}/dashboard/consistency`,
    generateConsistency:  (projectId: string) => `/api/projects/${projectId}/dashboard/consistency/generate`,
  },
} as const;
